import measurementDiagramsConverter, {
	DiagramsType,
} from 'helper/converter/measurement-diagrams-converter/measurement-diagrams-converter';
import { DISPLAY_FORMATS, format } from 'helper/dateTime';
import { GenderKeyType } from '../../types/GenderKey';
import { mapCategoryIcon } from '../programItems';
import { getPreviewImageByGender, ProgramOverviewItemType } from '../programOverview';
import { RootStateType } from './dashboard';
import {
	ActivitiesType,
	DaysStatsType,
	MeasurementDiagramTypeType,
	NormalizedTrackingHistoryItemType,
	TodoStatsType,
	TodoType,
	TrackingHistoryItemType,
	TrackingHistoryType,
} from './dashboard';

export const getProgram = (rootState: RootStateType) => rootState.dashboard.program;

export const getPreviewImage = (rootState: RootStateType, gender: GenderKeyType | null) => {
	const program = getProgram(rootState) as Partial<ProgramOverviewItemType>;
	return program ? getPreviewImageByGender(program, gender) : null;
};

export const getOverview = (rootState: RootStateType) => rootState.dashboard.overview;

export const getTrackingHistoryList = (rootState: RootStateType) =>
	(getOverview(rootState) || ({} as any)).trackingHistory || [];

/**
 * Sorts a list of todos to ensure that completed todos are at the end of the list.
 *
 * @param {any} a
 * @param {any} b
 * @returns boolean
 */
const sortByCompletion = (a: { done: boolean }, b: { done: boolean }): number => {
	if (a.done && !b.done) {
		return 1;
	}

	if (!a.done && b.done) {
		return -1;
	}

	return 0;
};

/**
 * Returns whether a product is of type recommendation (ProductProgramItem) or dynamicProduct(DynamicProductProgramItem).
 * @param { any } todo
 */
const filterProducts = (todo: TodoType): boolean => todo.type !== 'recommendation' && todo.type !== 'dynamicProduct';

const calculatePercentage = (a: number, b: number): number => {
	if (a === null || b === null || b === 0) {
		return 0;
	}

	return Math.round((a / b) * 100);
};

/**
 * Returns a list of all of the todos mapped to the corresponding categories,
 * all products filtered and then sorted by whether they've been completed or not.
 */
export const getTodos = (rootState: RootStateType) => {
	return rootState.dashboard.todos
		.map(({ id, type, done }) => ({
			id,
			done,
			type,
			icon: mapCategoryIcon(type || ''),
		}))
		.filter(filterProducts)
		.sort(sortByCompletion);
};

/**
 * Returns the amount of burnt calories.
 * @param { Object } state
 * @returns { number }
 */
export const getCalories = (rootState: RootStateType): number | null => {
	const { calories } = getOverview(rootState) || ({} as any);
	return calories || null;
};

/*
 * Returns the stats of the program's todos (total and done) along with the percentage
 * of completed todos.
 *
 * @param { Object } state
 * @returns { Object } Tiny Stats DTO
 */
export const getTodoStats = (rootState: RootStateType): TodoStatsType | null => {
	if (!rootState.dashboard || !rootState.dashboard.overview) {
		return null;
	}

	const { totalTodos, totalTodosDone } = getOverview(rootState) || ({} as any);

	if (totalTodos === null || totalTodosDone === null) {
		return null;
	}

	return {
		total: totalTodos,
		done: totalTodosDone,
		percentage: calculatePercentage(totalTodosDone, totalTodos) || 0,
	};
};

/*
 * Returns completed activities for the stats.
 * @param { Object } state
 * @returns { Array } Tiny Stats DTO
 */
export const getActivities = (rootState: RootStateType): ActivitiesType => {
	if (!rootState.dashboard.overview) {
		return [];
	}

	const keys = ['numberOfRecipesDone', 'numberOfWorkoutsDone', 'numberOfTipsDone', 'numberOfTodosDone'];

	const typeMap: { [key: string]: string } = {
		numberOfRecipesDone: 'recipe',
		numberOfWorkoutsDone: 'workout',
		numberOfTipsDone: 'tip',
		numberOfTodosDone: 'task',
	};

	return keys.map((key: string) => ({
		type: typeMap[key] ? typeMap[key] : '',
		count: (getOverview(rootState) || {})[key],
	}));
};

export const getDaysStats = (rootState: RootStateType): DaysStatsType | null => {
	const overallNumberOfDays = (getOverview(rootState) || ({} as any)).overallNumberOfDays || null;
	const currentDayNumber = (getOverview(rootState) || ({} as any)).currentDayNumber || null;

	if (overallNumberOfDays === null || currentDayNumber === null) {
		return null;
	}

	return {
		total: overallNumberOfDays,
		done: currentDayNumber,
		percentage: calculatePercentage(currentDayNumber, overallNumberOfDays),
	};
};

export const getCoachMotivation = (rootState: RootStateType): string | null =>
	(getOverview(rootState) || ({} as any)).coachMotivation || null;

export const getTrackingHistory = (rootState: RootStateType): TrackingHistoryType => {
	if (!rootState.dashboard.overview) {
		return [];
	}

	return getTrackingHistoryList(rootState).map((item: TrackingHistoryItemType) => {
		return {
			label: format((item.dateTime || ({} as any)).date || '', DISPLAY_FORMATS.SHORT_DATE, 'YYYY-MM-DD'),
			value: item.weight || 0,
		};
	});
};

export const getRangeDifference = (rootState: RootStateType): number | null => {
	if (!rootState.dashboard.overview) {
		return null;
	}

	const { desiredWeight } = getOverview(rootState) || ({} as any);

	return getTrackingHistoryList(rootState).reduce((pre, cur) => {
		const difference = Math.abs((cur.weight || 0) - (parseFloat(desiredWeight) || 0));

		if (difference > pre) {
			return difference;
		}

		return pre;
	}, 0);
};

export const getInitialWeightLost = (rootState: RootStateType): number =>
	(getOverview(rootState) || ({} as any)).initialWeightLost || 0;
export const getAnamnesisTimestamp = (rootState: RootStateType): Object =>
	(getOverview(rootState) || ({} as any)).anamnesisTimestamp || {};
export const getHealthGoal = (rootState: RootStateType): string =>
	(getOverview(rootState) || ({} as any)).healthGoal || '';
export const getCurrentWeight = (rootState: RootStateType): number | null =>
	(getOverview(rootState) || ({} as any)).currentWeight || null;
export const getDesiredWeight = (rootState: RootStateType): number =>
	(getOverview(rootState) || ({} as any)).desiredWeight || 0;
export const getWeightReduceGoal = (rootState: RootStateType): number =>
	(getOverview(rootState) || ({} as any)).weightReduceGoal || 0;
export const getWeightLost = (rootState: RootStateType): number =>
	(getOverview(rootState) || ({} as any)).weightLost || 0;

export const getMeasurement = (rootState: RootStateType): NormalizedTrackingHistoryItemType | undefined => {
	if (!rootState.dashboard.measurement) {
		return;
	}

	const measurement: TrackingHistoryItemType = rootState.dashboard.measurement || ({} as any);
	const dateTime = measurement.dateTime || ({} as any);

	return {
		...measurement,
		time: dateTime.time,
		date: dateTime.date,
	};
};

export const getMeasurmentDiagramSelectedType = (rootState: RootStateType): MeasurementDiagramTypeType =>
	rootState.dashboard.measurementDiagram.selectedType || 'WEIGHT';
export const getMeasurmentDiagramUpdateKey = (rootState: RootStateType): string =>
	rootState.dashboard.measurementDiagram.updateKey || 'RECEIVED_WEIGHT';
export const getIsMeasurmentDiagramLoading = (rootState: RootStateType): boolean =>
	rootState.dashboard.measurementDiagram.loading;
export const getMeasurmentDiagrams = (rootState: RootStateType): DiagramsType =>
	measurementDiagramsConverter((rootState.dashboard.measurementDiagram || ({} as any)).diagram || []);
